import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Paper,
  TextInput,
  Textarea,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useMachineCategories,
  useCreateMachineCategory,
  useUpdateMachineCategory,
  useDeleteMachineCategory,
} from "../../../hooks/admin/useMachineCategories";
import { MachineCategory } from "../../../services/admin/machineCategories.service";

const ITEMS_PER_PAGE = 20;

export default function MachineCategoriesAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  const [editing, setEditing] = useState<MachineCategory | null>(null);
  const [itemToDelete, setItemToDelete] = useState<MachineCategory | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const { data: categories = [], isLoading, refetch } = useMachineCategories();
  const createCategory = useCreateMachineCategory();
  const updateCategory = useUpdateMachineCategory();
  const deleteCategory = useDeleteMachineCategory();

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
    },
    validate: {
      name: (v: string) => (v.trim() ? null : "Name is required"),
    },
  });

  const filteredData = useMemo(() => {
    if (!debouncedSearch) return categories;
    const q = debouncedSearch.toLowerCase();
    return categories.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q)
    );
  }, [categories, debouncedSearch]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const handleAdd = () => {
    setEditing(null);
    form.reset();
    openFormModal();
  };

  const handleEdit = (item: MachineCategory) => {
    setEditing(item);
    form.setValues({
      name: item.name,
      description: item.description || "",
    });
    openFormModal();
  };

  const handleSubmit = async (values: typeof form.values) => {
    const data = {
      name: values.name,
      description: values.description || undefined,
    };

    if (editing) {
      await updateCategory.mutateAsync({ id: editing.id, ...data });
    } else {
      await createCategory.mutateAsync(data);
    }
    closeFormModal();
    form.reset();
  };

  const handleDelete = (item: MachineCategory) => {
    setItemToDelete(item);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await deleteCategory.mutateAsync(itemToDelete.id);
      closeDeleteModal();
      setItemToDelete(null);
    }
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Machine Categories</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage equipment and machine categories for grouping (e.g., IHC
                Stainer, H&E Stainer)
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search */}
          <Paper p="md" withBorder>
            <Group>
              <TextInput
                placeholder="Search by name or description..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 1 }}
              />
              <Button
                variant="subtle"
                onClick={() => refetch()}
                loading={isLoading}
                leftSection={<IconRefresh size={16} />}
              >
                Refresh
              </Button>
            </Group>
          </Paper>

          {/* Table */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Machine Categories ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search
                    ? "No categories match the current search"
                    : "No machine categories found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {item.description || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ width: 100 }}>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(item)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editing ? "Edit Machine Category" : "Add Machine Category"}
          size="md"
          closeOnClickOutside={false}
        >
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Name"
                required
                withAsterisk
                placeholder="e.g., IHC Stainer"
                {...form.getInputProps("name")}
              />
              <Textarea
                label="Description"
                placeholder="e.g., Immunohistochemistry staining equipment"
                minRows={3}
                {...form.getInputProps("description")}
              />

              <Group justify="flex-end" mt="md" gap="xs">
                <Button variant="default" onClick={closeFormModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createCategory.isPending || updateCategory.isPending}
                >
                  {editing ? "Update" : "Create"}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Delete Machine Category"
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to delete{" "}
              <strong>{itemToDelete?.name}</strong>?
            </Text>
            <Text size="sm" c="dimmed">
              This action cannot be undone. Machines assigned to this category
              will become uncategorized.
            </Text>
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteCategory.isPending}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
